import { useEffect, useState } from 'react';
import {
  MoreVertical,
  FileText,
  Bookmark,
  Users,
  Pencil,
  ArrowUpRight,
  Clock,
  Download,
  Trash2,
  PhoneCall,
} from '@/shared/ui/icons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../shared/ui/dropdown-menu';
import { useCall } from '../../features/calls/CallContext';
import { useOptionalHuddle } from '../../features/calls/HuddleContext';
import { HuddleButton } from '../../features/calls/HuddleButton';
import { supportsBrowserHuddles, type HuddleConversation } from '../../features/calls/huddleConversation';
import { DataService } from '../../services/DataService';
import { NativeBridge } from '../../services/NativeBridge';
import { isNativeApp } from '../../utils/nativeApp';
import type { User } from '../../types/database';

/** Conversation actions for layouts whose global header owns the chat. */
export interface ChatActionsState {
  available: boolean;
  conversationHeaderOwnsActions: boolean;
  canViewMembers: boolean;
  showMembersPanel: boolean;
  hasManagementActions: boolean;
  canRenameConversation: boolean;
  canOpenProject: boolean;
  /** True only for direct (1:1) chats — controls call button visibility. */
  isDirectChat: boolean;
  /** The other participant's user ID, used to initiate a call. */
  dmOtherUserId: string | null;
  /** Legacy bridge field, now also used for eligible member direct messages. */
  huddleChannel?: HuddleConversation | null;
}

const INITIAL_STATE: ChatActionsState = {
  available: false,
  conversationHeaderOwnsActions: false,
  canViewMembers: false,
  showMembersPanel: false,
  hasManagementActions: false,
  canRenameConversation: false,
  canOpenProject: false,
  isDirectChat: false,
  dmOtherUserId: null,
  huddleChannel: null,
};

export function MessagesChatActionsPill({ pillSize = 36, color = 'var(--foreground)' }: { pillSize?: number; color?: string }) {
  const [state, setState] = useState<ChatActionsState>(INITIAL_STATE);
  const { status: callStatus, startCall } = useCall();
  const huddle = useOptionalHuddle();
  const browserHuddle = Boolean(state.huddleChannel && huddle && supportsBrowserHuddles());
  const canCall = callStatus === 'idle' && !huddle?.isInHuddle;

  useEffect(() => {
    const onState = (event: Event) => {
      const detail = (event as CustomEvent<ChatActionsState>).detail;
      if (!detail) {
        setState(INITIAL_STATE);
        return;
      }
      setState({ ...INITIAL_STATE, ...detail });
    };
    window.addEventListener('messages:chat-actions-state', onState);
    window.dispatchEvent(new CustomEvent('messages:request-chat-actions-state'));
    return () => window.removeEventListener('messages:chat-actions-state', onState);
  }, []);

  const dispatch = (name: string) => () => {
    window.dispatchEvent(new CustomEvent(name));
  };

  // iOS web-shell: this pill still renders and owns the data/actions (state
  // above), but the tapped menu is a native action sheet, not this web
  // DropdownMenu — same "web requests the sheet, web still persists" split
  // ChatWindow's own kebab uses for the same action set when native owns the
  // header. presentNativeMenu is a no-op on browser/macOS (see NativeBridge),
  // so this only takes over on iOS.
  useEffect(() => {
    if (!isNativeApp()) return;
    const handleMenuAction = (event: Event) => {
      const id = (event as CustomEvent<{ id: string }>).detail?.id;
      switch (id) {
        case 'members': dispatch('messages:action:toggle-members')(); break;
        case 'rename': dispatch('messages:action:rename')(); break;
        case 'open-project': dispatch('messages:action:open-project')(); break;
        case 'scheduled': dispatch('messages:action:scheduled')(); break;
        case 'export': dispatch('messages:action:export')(); break;
        case 'delete': dispatch('messages:action:delete')(); break;
      }
    };
    window.addEventListener('quantis:native:menuAction', handleMenuAction);
    return () => window.removeEventListener('quantis:native:menuAction', handleMenuAction);
  }, [state]);

  const presentNativeConversationMenu = () => {
    const items: Array<{ id: string; label: string; sfSymbol?: string; destructive?: boolean }> = [];
    if (state.canViewMembers) {
      items.push({ id: 'members', label: state.showMembersPanel ? 'Hide Members' : 'Show Members', sfSymbol: 'person.2' });
    }
    if (state.canRenameConversation) {
      items.push({ id: 'rename', label: 'Rename Conversation', sfSymbol: 'pencil' });
    }
    if (state.canOpenProject) {
      items.push({ id: 'open-project', label: 'Open Project', sfSymbol: 'folder' });
    }
    items.push({ id: 'scheduled', label: 'Scheduled Messages', sfSymbol: 'clock' });
    items.push({ id: 'export', label: 'Export Conversation', sfSymbol: 'square.and.arrow.down' });
    items.push({ id: 'delete', label: 'Delete Conversation', sfSymbol: 'trash', destructive: true });
    NativeBridge.presentNativeMenu({ items });
  };

  if (!state.available || state.conversationHeaderOwnsActions) return null;

  const callButton = (() => {
    if (browserHuddle || !state.isDirectChat || !state.dmOtherUserId) return null;
    const remoteUser = DataService.getUsers().find((u: User) => u.id === state.dmOtherUserId);
    if (!remoteUser) return null;
    return (
      <button
        type="button"
        disabled={!canCall}
        title={canCall ? `Call ${remoteUser.displayName || remoteUser.name}` : 'Already in a call'}
        onClick={() => canCall && startCall(remoteUser)}
        className="active:scale-90 transition-transform cursor-pointer"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: pillSize,
          height: pillSize,
          background: 'transparent',
          borderRadius: 999,
          border: 'none',
          color,
          opacity: canCall ? 1 : 0.4,
          cursor: canCall ? 'pointer' : 'default',
        }}
        onMouseEnter={(e) => { if (canCall) e.currentTarget.style.background = 'var(--header-pill-fill)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <PhoneCall style={{ width: 17, height: 17, strokeWidth: 2 }} />
      </button>
    );
  })();

  return (
    // AppLayout applies pointer-events:none to the floating header container so
    // it never blocks scrollable content beneath it. Every interactive pill must
    // restore pointer-events here; renderMobileHeaderAction does this for most
    // pills, but MessagesChatActionsPill is mounted directly (not via that
    // helper), so we must set it explicitly.
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'auto' }}>
      {callButton}
      {browserHuddle && state.huddleChannel && <HuddleButton channel={state.huddleChannel} size={pillSize} color={color} />}
      {isNativeApp() ? (
        <button
          type="button"
          aria-label="Conversation actions"
          className="active:scale-90 transition-transform cursor-pointer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: pillSize,
            height: pillSize,
            background: 'transparent',
            borderRadius: 999,
            border: 'none',
            color,
          }}
          onClick={presentNativeConversationMenu}
        >
          <MoreVertical style={{ width: 17, height: 17, strokeWidth: 2 }} />
        </button>
      ) : (
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Conversation actions"
          className="active:scale-90 transition-transform cursor-pointer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: pillSize,
            height: pillSize,
            background: 'transparent',
            borderRadius: 999,
            border: 'none',
            color,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--header-pill-fill)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <MoreVertical style={{ width: 17, height: 17, strokeWidth: 2 }} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="font-apple"
        style={{
          width: 220,
          borderRadius: 'var(--radius-popover)',
          padding: 6,
          background: 'var(--popover)',
          borderColor: 'var(--border)',
          boxShadow: 'var(--elevation-sm)',
        }}
      >
        {([
          ['files', 'Shared files', FileText],
          ['saved', 'Saved messages', Bookmark],
        ] as const).map(([view, label, Icon]) => (
          <DropdownMenuItem key={view} className="cursor-pointer gap-2.5 font-apple" onClick={() => window.dispatchEvent(new CustomEvent('messages:browse-conversation', { detail: { view } }))}>
            <Icon className="h-4 w-4" />{label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        {state.canViewMembers && (
          <DropdownMenuItem
            className="cursor-pointer font-apple gap-2.5"
            style={{
              borderRadius: 'var(--radius)',
              padding: '8px 10px',
              fontSize: 'var(--text-menu)',
              color: state.showMembersPanel ? 'var(--primary)' : undefined,
            }}
            onClick={dispatch('messages:action:toggle-members')}
          >
            <Users style={{ width: 16, height: 16 }} />
            {state.showMembersPanel ? 'Hide members' : 'Show members'}
          </DropdownMenuItem>
        )}
        {state.canRenameConversation && (
          <DropdownMenuItem
            className="cursor-pointer font-apple gap-2.5"
            style={{ borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: 'var(--text-menu)' }}
            onClick={dispatch('messages:action:rename')}
          >
            <Pencil style={{ width: 16, height: 16 }} />
            Rename conversation
          </DropdownMenuItem>
        )}
        {state.canOpenProject && (
          <DropdownMenuItem
            className="cursor-pointer font-apple gap-2.5"
            style={{ borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: 'var(--text-menu)' }}
            onClick={dispatch('messages:action:open-project')}
          >
            <ArrowUpRight style={{ width: 16, height: 16 }} />
            Open project
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          className="cursor-pointer font-apple gap-2.5"
          style={{ borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: 'var(--text-menu)' }}
          onClick={dispatch('messages:action:scheduled')}
        >
          <Clock style={{ width: 16, height: 16 }} />
          Scheduled messages
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer font-apple gap-2.5"
          style={{ borderRadius: 'var(--radius)', padding: '8px 10px', fontSize: 'var(--text-menu)' }}
          onClick={dispatch('messages:action:export')}
        >
          <Download style={{ width: 16, height: 16 }} />
          Export conversation
        </DropdownMenuItem>
        {(state.canViewMembers || state.hasManagementActions) && (
          <DropdownMenuSeparator style={{ background: 'var(--border)', margin: '4px 0' }} />
        )}
        <DropdownMenuItem
          className="cursor-pointer font-apple gap-2.5"
          style={{
            borderRadius: 'var(--radius)',
            padding: '8px 10px',
            fontSize: 'var(--text-menu)',
            color: 'var(--destructive)',
          }}
          onClick={dispatch('messages:action:delete')}
        >
          <Trash2 style={{ width: 16, height: 16 }} />
          Delete conversation
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
      )}
    </div>
  );
}
